<template>
    <main role="main" class="nl-v2">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('topbar.notifications.title') }}</div>
        </portal>

        <header class="nl-hero">
            <div class="nl-hero-icon">
                <i class="fal fa-bell"></i>
            </div>
            <div class="nl-hero-body">
                <h1 class="nl-hero-title">{{ $t('topbar.notifications.title') }}</h1>
                <p class="nl-hero-sub">
                    <span>{{ totalLabel }}</span>
                    <template v-if="unreadCount > 0">
                        <span class="nl-hero-sub-sep" aria-hidden="true">·</span>
                        <span class="nl-hero-sub-pill">
                            {{ $t('topbar.notifications.unread_count', { count: unreadCount }) }}
                        </span>
                    </template>
                </p>
            </div>
            <div class="nl-hero-actions">
                <div class="nl-tabs">
                    <button
                        type="button"
                        class="nl-tab"
                        :class="{ 'nl-tab--active': filter === 'all' }"
                        @click="setFilter('all')"
                    >
                        {{ $t('notifications.page.tab_all') }}
                        <span class="nl-tab-count">{{ notifications.length }}</span>
                    </button>
                    <button
                        type="button"
                        class="nl-tab"
                        :class="{ 'nl-tab--active': filter === 'unread' }"
                        @click="setFilter('unread')"
                    >
                        {{ $t('notifications.page.tab_unread') }}
                        <span v-if="unreadCount > 0" class="nl-tab-count nl-tab-count--accent">
                            {{ unreadCount }}
                        </span>
                    </button>
                </div>
                <BmvButton
                    v-if="unreadCount > 0"
                    variant="ghost"
                    size="sm"
                    icon="fal fa-check-double"
                    @click="markAllRead"
                >
                    {{ $t('topbar.notifications.mark_all_read') }}
                </BmvButton>
            </div>
        </header>

        <div v-if="loading" class="nl-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <div v-else-if="paginated.length" class="nl-list">
            <button
                v-for="n in paginated"
                :key="n.id"
                type="button"
                class="nl-item"
                :class="{ 'is-unread': !n.read }"
                @click="onItemClick(n)"
            >
                <span class="nl-item-icon" :class="`nl-item-icon--${n.kind || 'info'}`">
                    <i :class="iconClass(n)"></i>
                </span>
                <div class="nl-item-body">
                    <span class="nl-item-title">{{ n.title }}</span>
                    <span class="nl-item-meta">
                        <template v-if="n.postId">
                            <span class="nl-item-ref">
                                {{ $t('topbar.notifications.ticket_ref', { id: n.postId }) }}
                            </span>
                            <span class="nl-item-sep" aria-hidden="true">·</span>
                        </template>
                        <span>{{ formatTime(n.createdAt) }}</span>
                    </span>
                </div>
                <span v-if="!n.read" class="nl-item-dot" aria-hidden="true"></span>
                <i class="fal fa-chevron-right nl-item-arrow"></i>
            </button>
        </div>

        <div v-else class="nl-empty">
            <i class="fal fa-bell-slash nl-empty-icon"></i>
            <h3 class="nl-empty-title">{{ $t('topbar.notifications.empty_title') }}</h3>
            <p class="nl-empty-desc">{{ $t('topbar.notifications.empty_desc') }}</p>
        </div>

        <nav v-if="!loading && totalPages > 1" class="nl-pagination">
            <button
                type="button"
                class="nl-page-btn"
                :disabled="page === 1"
                @click="page = page - 1"
            >
                <i class="fal fa-chevron-left"></i>
                <span>{{ $t('notifications.page.previous') }}</span>
            </button>
            <span class="nl-page-info">
                {{ $t('notifications.page.page_of', { page, total: totalPages }) }}
            </span>
            <button
                type="button"
                class="nl-page-btn"
                :disabled="page === totalPages"
                @click="page = page + 1"
            >
                <span>{{ $t('notifications.page.next') }}</span>
                <i class="fal fa-chevron-right"></i>
            </button>
        </nav>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState, mapActions } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'

export default {
    name: 'NotificationsList',
    components: { BmvButton },
    data() {
        return {
            filter: 'all',
            page: 1,
            perPage: 20,
            loading: false
        }
    },
    computed: {
        ...mapState('notifications', { notifications: (s) => s.list }),
        unreadCount() {
            return this.notifications.filter((n) => !n.read).length
        },
        filtered() {
            if (this.filter === 'unread') {
                return this.notifications.filter((n) => !n.read)
            }
            return this.notifications
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filtered.length / this.perPage))
        },
        paginated() {
            const start = (this.page - 1) * this.perPage
            return this.filtered.slice(start, start + this.perPage)
        },
        totalLabel() {
            const n = this.filtered.length
            return this.$t('notifications.page.count', { count: n })
        }
    },
    watch: {
        filter() {
            this.page = 1
        },
        totalPages(newVal) {
            if (this.page > newVal) this.page = newVal
        }
    },
    created() {
        this.loading = true
        this.$store
            .dispatch('notifications/fetchAll')
            .finally(() => {
                this.loading = false
            })
    },
    methods: {
        ...mapActions('notifications', ['markRead', 'markAllRead']),
        setFilter(f) {
            this.filter = f
        },
        onItemClick(n) {
            if (!n.read) this.markRead(n.id)
            if (n.link) {
                this.$router.push(n.link).catch(() => {})
            }
        },
        iconClass(n) {
            if (n.icon) return n.icon
            switch (n.kind) {
                case 'success': return 'fal fa-check-circle'
                case 'warning': return 'fal fa-exclamation-triangle'
                case 'danger': return 'fal fa-times-circle'
                case 'message': return 'fal fa-comment-alt'
                case 'ticket': return 'fal fa-ticket'
                default: return 'fal fa-info-circle'
            }
        },
        formatTime(iso) {
            if (!iso) return ''
            const date = new Date(iso)
            if (Number.isNaN(date.getTime())) return ''
            const diff = (Date.now() - date.getTime()) / 1000
            if (diff < 60) return this.$t('topbar.notifications.time_now')
            if (diff < 3600) {
                const mins = Math.floor(diff / 60)
                return this.$t('topbar.notifications.time_minutes', { count: mins })
            }
            if (diff < 86400) {
                const hours = Math.floor(diff / 3600)
                return this.$t('topbar.notifications.time_hours', { count: hours })
            }
            const locale = (this.$i18n && this.$i18n.locale) || 'fr'
            return date.toLocaleDateString(locale, {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            })
        }
    }
}
</script>

<style scoped>
.nl-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

/* Hero */
.nl-hero {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-5);
}

.nl-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-lg);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.nl-hero-icon i {
    color: inherit !important;
}

.nl-hero-body {
    flex: 1;
    min-width: 0;
}

.nl-hero-title {
    margin: 0 0 2px;
    font-size: 1.35rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.nl-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.nl-hero-sub-sep {
    color: var(--v2-text-4);
}

.nl-hero-sub-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.nl-hero-actions {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-3);
}

/* Tabs */
.nl-tabs {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
}

.nl-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast);
}

.nl-tab:hover {
    color: var(--v2-text-1);
}

.nl-tab--active {
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    box-shadow: var(--v2-shadow-sm);
}

.nl-tab-count {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    padding: 1px 8px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
}

.nl-tab-count--accent {
    background: var(--v2-primary);
    color: #fff;
}

/* Loading */
.nl-loading {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-6);
    color: var(--v2-text-3);
    font-size: 1.5rem;
}

/* List */
.nl-list {
    display: flex;
    flex-direction: column;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.nl-item {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 16px 18px;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--v2-border-subtle);
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: var(--v2-text-1);
    transition: background var(--v2-transition-fast);
}

.nl-item:last-child {
    border-bottom: none;
}

.nl-item:hover {
    background: var(--v2-surface-1);
}

.nl-item.is-unread {
    background: var(--v2-primary-soft);
}

.nl-item.is-unread:hover {
    background: var(--v2-primary-soft);
    filter: brightness(0.97);
}

.nl-item-icon {
    width: 38px;
    height: 38px;
    border-radius: var(--v2-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
}

.nl-item-icon i {
    color: inherit !important;
}

.nl-item-icon--info {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.nl-item-icon--success {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.nl-item-icon--warning {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

.nl-item-icon--danger {
    background: rgba(239, 68, 68, 0.12);
    color: #b91c1c;
}

.nl-item-icon--message {
    background: rgba(99, 102, 241, 0.12);
    color: #4338ca;
}

.nl-item-icon--ticket {
    background: var(--v2-surface-2);
    color: var(--v2-text-2);
}

.nl-item-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.nl-item-title {
    font-size: 0.92rem;
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-1);
    line-height: 1.4;
}

.nl-item-meta {
    font-size: 0.76rem;
    color: var(--v2-text-3);
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.nl-item-ref {
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-2);
}

.nl-item-sep {
    color: var(--v2-text-4);
}

.nl-item-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v2-primary);
    flex-shrink: 0;
}

.nl-item-arrow {
    color: var(--v2-text-4);
    font-size: 0.78rem;
    flex-shrink: 0;
    transition: transform var(--v2-transition-fast), color var(--v2-transition-fast);
}

.nl-item:hover .nl-item-arrow {
    color: var(--v2-primary);
    transform: translateX(2px);
}

/* Empty */
.nl-empty {
    text-align: center;
    padding: 56px 24px;
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.nl-empty-icon {
    font-size: 2.4rem;
    color: var(--v2-text-4);
    display: block;
    margin-bottom: 12px;
}

.nl-empty-title {
    margin: 0 0 6px;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
}

.nl-empty-desc {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
}

/* Pagination */
.nl-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--v2-space-4);
    padding: 4px 2px;
}

.nl-page-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 14px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast), color var(--v2-transition-fast);
}

.nl-page-btn:hover:not(:disabled) {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.nl-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.nl-page-btn i {
    font-size: 0.72rem;
    color: inherit !important;
}

.nl-page-info {
    font-size: 0.82rem;
    color: var(--v2-text-3);
    font-variant-numeric: tabular-nums;
}

@media (max-width: 640px) {
    .nl-v2 {
        padding: 18px 16px 32px;
    }

    .nl-hero {
        flex-wrap: wrap;
    }

    .nl-hero-actions {
        order: 3;
        flex-basis: 100%;
        margin-top: 6px;
        flex-wrap: wrap;
        gap: 8px;
    }

    .nl-item {
        padding: 14px 14px;
        gap: 10px;
    }

    .nl-item-arrow {
        display: none;
    }

    .nl-pagination {
        flex-direction: column;
        gap: 10px;
    }

    .nl-page-btn {
        width: 100%;
        justify-content: center;
    }
}
</style>
